/**
 * useBusinessDerivation — Business entity-group derivation engine.
 *
 * Analogue of `useIndividualDerivation`, but tuned to how the Business group
 * actually resolves customerType: the officer **manually picks** the sub-type,
 * and a handful of flags on the record (isNonProfit, sole-prop w/o EIN) flip
 * the downstream Call Class + TIN Code.
 *
 * Per `docs/port-design/category-cards/business.md` §"Derivation rules":
 *   - `customerType` is manually selected — we reflect the picker value, we do
 *     NOT compute it from other fields.
 *   - `callClass` derives from the sub-type picker (CALL_CLASS_MAP) with the
 *     Non-Profit flag flipping Corp (20) → NonProfit-coded (21).
 *   - `tinCode` is locked to `'E'` for all Business customerTypes except DBA
 *     sole-prop (no EIN → `'S'` + owner's SSN).
 *   - Supervisor approval is required on CIF add for Corp/LLC/Partnership/
 *     NonProfit (seed 11-entity-rules.sql:229-236).
 *
 * Shape matches `DerivationResult` from useIndividualDerivation so the Identity
 * section can render its "Entity Type" chip + supervisor-approval banner with
 * the same sub-components.
 */

import { useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import type { RaoPerson } from '../../../types';
import { SUPERVISOR_APPROVAL_SUBTYPES } from './business-config';

// -----------------------------------------------------------------------------
// Public API (mirrors useIndividualDerivation)
// -----------------------------------------------------------------------------

/** The 9 Business sub-types per business.md §1 plus the short-form alias `Corp`. */
export type BusinessCustomerType =
  | 'Corporation'
  | 'Corp'
  | 'LLC'
  | 'DBA'
  | 'SoleProp'
  | 'Partnership'
  | 'NonProfit'
  | 'UnincorpAssoc'
  | 'IOLTA'
  | 'Political'
  | 'ICS_Business';

/**
 * Source of the derivation decision. `picker` covers the normal flow where
 * the officer chose a sub-type from the dropdown. `stored` is returned for
 * existing CIFs where we simply echo back what Core Director has on file.
 * `baseline` appears before the officer has picked anything.
 */
export type BusinessDerivationSource = 'picker' | 'stored' | 'baseline';

export interface BusinessDerivationResult {
  /** The auto-derived customer type for this entity. */
  readonly customerType: BusinessCustomerType;
  /** Which input drove the derivation. */
  readonly derivedFrom: BusinessDerivationSource;
  /**
   * Present when the picker value differs from the stored Core Director type
   * (existing-CIF edit flow) — UI renders "updated from 'X'" chip.
   */
  readonly promotedFrom?: string;
  /**
   * True when this CIF needs a supervisor to approve before save. Non-Profit
   * / Corp / LLC / Partnership all trip this on new-CIF add per the seed.
   */
  readonly requiresSupervisorApproval?: boolean;
}

export interface UseBusinessDerivationOptions {
  /** 'new' = fresh capture; 'existing' = CustInq reload. */
  readonly mode: 'new' | 'existing';
  /** The customerType Core Director had on file (existing only). */
  readonly storedType?: string;
}

// -----------------------------------------------------------------------------
// Officer supervisor-role probe (copy of Individual version — keep in sync)
// -----------------------------------------------------------------------------

function isSupervisor(auth: ReturnType<typeof useAuth>): boolean {
  const user: unknown = (auth as unknown as { user?: unknown })?.user;
  if (!user || typeof user !== 'object') return false;
  const u = user as Record<string, unknown>;
  if (u.isSupervisor === true) return true;
  if (typeof u.role === 'string' && u.role.toLowerCase().includes('supervisor')) return true;
  const roles = u.roles;
  if (Array.isArray(roles)) {
    return (roles as unknown[]).some(
      (r) => typeof r === 'string' && r.toLowerCase().includes('supervisor'),
    );
  }
  return false;
}

// -----------------------------------------------------------------------------
// Hook
// -----------------------------------------------------------------------------

/**
 * Compute the Business-group `customerType` + supervisor-gate signal.
 *
 * Memoized on `person.customerType` (the picker value), `person.extra.isNonProfit`
 * (the flag that flips Corp → NonProfit Call Class at save time — UI toggle
 * lives in the Identity section), the mode, and the stored type.
 */
export function useBusinessDerivation(
  person: RaoPerson,
  opts: UseBusinessDerivationOptions,
): BusinessDerivationResult {
  const auth = useAuth();

  return useMemo<BusinessDerivationResult>(() => {
    const officerIsSupervisor = isSupervisor(auth);
    const pickerValue = (person.customerType ?? '') as string;
    const isNonProfitFlag = Boolean(person.extra?.isNonProfit);

    // Rule 1: baseline — no picker value yet
    if (!pickerValue) {
      return {
        // Default to 'Corporation' for the chip so the UI doesn't show a blank
        // Entity Type pill, but flag it as 'baseline' so the "derived from"
        // copy reads sensibly ("new entity baseline").
        customerType: 'Corporation' as BusinessCustomerType,
        derivedFrom: 'baseline',
      };
    }

    // Rule 2: Non-Profit flag flips Corp → NonProfit (Call Class 20 → 21).
    // NOTE: this only changes the effective customerType when the picker is
    // on `Corporation` — for explicit `NonProfit` picks the flag is redundant.
    let effective = pickerValue as BusinessCustomerType;
    if ((pickerValue === 'Corporation' || pickerValue === 'Corp') && isNonProfitFlag) {
      effective = 'NonProfit';
    }

    const requiresSupervisorApproval =
      opts.mode === 'new' &&
      SUPERVISOR_APPROVAL_SUBTYPES.has(effective) &&
      !officerIsSupervisor;

    // Existing-CIF flow — if stored differs, surface the promotedFrom chip.
    if (opts.mode === 'existing' && opts.storedType && opts.storedType !== effective) {
      return {
        customerType: effective,
        derivedFrom: 'picker',
        promotedFrom: opts.storedType,
        ...(requiresSupervisorApproval ? { requiresSupervisorApproval: true } : {}),
      };
    }

    return {
      customerType: effective,
      derivedFrom: opts.storedType && opts.mode === 'existing' ? 'stored' : 'picker',
      ...(requiresSupervisorApproval ? { requiresSupervisorApproval: true } : {}),
    };
  }, [person.customerType, person.extra?.isNonProfit, opts.mode, opts.storedType, auth]);
}
